import { Injectable } from '@angular/core';
import { DirectionService } from './direction.service';
import { of, switchMap } from 'rxjs';

/**
 * Single source of truth for sidebar tab labels and in-page titles.
 * Keys used by routes (`pageName`) and by the sidebar menu.
 */
export const NAV_LABELS = {
  ar: {
    Home: 'الرئيسية',
    Strategies: 'الاستراتيجيات',
    strategies: 'الاستراتيجيات',
    Strategy: 'الاستراتيجية',
    StrategySPillars: 'محاور الاستراتيجية',
    StrategySValues: 'قيم الاستراتيجية',
    StrategicGoals: 'استعراض الأهداف',
    StrategicIndicators: 'استعراض المؤشرات',
    Initiatives: 'استعراض المبادرات',
    FileExplorer: 'متصفح الأدلة',
    Performance: 'لوحة البيانات',
    OverallOperationalPerformance: 'الأداء التشغيلي العام',
    IndicatorSOutcomes: 'أداء المؤشرات',
    StrategyAlignment: 'مواءمة الاستراتيجية',
    OrganizationHierarchy: 'الهيكل التنظيمي',
    Reports: 'التقارير',
    ReviewMilestoneOutputs: 'مراجع مخرجات المبادرات',
    ReviewIndicatorsOutcomesProofs: 'مراجع أدلة المؤشرات',
    Profile: 'الملف الشخصي',
    Tasks: 'مهامي',
    ControlPanel: 'لوحة التحكم',
    Department: 'الإدارات والأقسام',
    Permissions: 'المستخدمين',
    Indicator: 'المؤشر',
    MyReports: 'تقاريري',
    DepartmentApprovals: 'اعتمادات الإدارة',
    StrategicKnowledgeLibrary: 'مكتبة المعرفة الاستراتيجية',
    ProjectEditPermissions: 'صلاحيات تعديل المشاريع',
    systemManagement: 'إدارة المنظومة',
    users: 'المستخدمين',
    departments: 'الإدارات',
    sections: 'الأقسام',
    defaultStrategy: 'الاستراتيجية الافتراضية',
    tasks: 'مهامي',
    MilestoneOutputAuditor: 'مراجع مخرجات المبادرات',
    IndicatorsOutputAuditor: 'مراجع أدلة المؤشرات',
    overallOperationalPerformance: 'الأداء التشغيلي العام',
    strategicDashboard: 'لوحة البيانات',
    indicatorsPerformance: 'أداء المؤشرات',
    strategyAlignment: 'مواءمة الاستراتيجية',
    organizationHierarchy: 'الهيكل التنظيمي',
    fileExplorer: 'متصفح الأدلة',
    reports: 'التقارير',
    myReports: 'تقاريري',
    departmentApprovals: 'اعتمادات الإدارة',
    viewGoals: 'استعراض الأهداف',
    viewIndicators: 'استعراض المؤشرات',
    viewMyIndicators: 'استعراض مؤشراتي',
    viewInitiatives: 'استعراض المبادرات',
    viewDepartmentInitiatives: 'استعراض مبادرات الإدارة',
    viewSectionInitiatives: 'استعراض مبادرات القسم',
    viewMyInitiatives: 'استعراض مبادراتي',
  },
  en: {
    Home: 'Home',
    Strategies: 'Strategies',
    strategies: 'Strategies',
    Strategy: 'Strategy',
    StrategySPillars: "Strategy's Pillars",
    StrategySValues: "Strategy's Values",
    StrategicGoals: 'View Goals',
    StrategicIndicators: 'View Indicators',
    Initiatives: 'View Initiatives',
    FileExplorer: 'File Explorer',
    Performance: 'Strategic Dashboard',
    OverallOperationalPerformance: 'Overall Operational Performance',
    IndicatorSOutcomes: 'Indicators Performance',
    StrategyAlignment: 'Strategy Alignment',
    OrganizationHierarchy: 'Organization Hierarchy',
    Reports: 'Reports',
    ReviewMilestoneOutputs: 'Milestone Output Auditor',
    ReviewIndicatorsOutcomesProofs: 'Indicators Output Auditor',
    Profile: 'Profile',
    Tasks: 'Tasks',
    ControlPanel: 'Control Panel',
    Department: 'Departments & Sections',
    Permissions: 'Users',
    Indicator: 'Indicator',
    MyReports: 'My Reports',
    DepartmentApprovals: 'Department Approvals',
    StrategicKnowledgeLibrary: 'Strategic Knowledge Library',
    ProjectEditPermissions: 'Project Edit Permissions',
    systemManagement: 'System Management',
    users: 'Users',
    departments: 'Departments',
    sections: 'Sections',
    defaultStrategy: 'Default Strategy',
    tasks: 'Tasks',
    MilestoneOutputAuditor: 'Milestone Output Auditor',
    IndicatorsOutputAuditor: 'Indicators Output Auditor',
    overallOperationalPerformance: 'Overall Operational Performance',
    strategicDashboard: 'Strategic Dashboard',
    indicatorsPerformance: 'Indicators Performance',
    strategyAlignment: 'Strategy Alignment',
    organizationHierarchy: 'Organization Hierarchy',
    fileExplorer: 'File Explorer',
    reports: 'Reports',
    myReports: 'My Reports',
    departmentApprovals: 'Department Approvals',
    viewGoals: 'View Goals',
    viewIndicators: 'View Indicators',
    viewMyIndicators: 'View My Indicators',
    viewInitiatives: 'View Initiatives',
    viewDepartmentInitiatives: 'View Department Initiatives',
    viewSectionInitiatives: 'View Section Initiatives',
    viewMyInitiatives: 'View My Initiatives',
  },
} as const;

export type NavLabelKey = keyof typeof NAV_LABELS.ar;

@Injectable({
  providedIn: 'root'
})
export class PageTitleService {

  constructor(private directionService: DirectionService) { }

  getLabel(pageName: string, dir: 'rtl' | 'ltr' | string = 'rtl'): string {
    const locale = dir === 'ltr' ? NAV_LABELS.en : NAV_LABELS.ar;
    return (locale as Record<string, string>)[pageName] || pageName;
  }

  getPageTitle(pageName: string) {
    return this.directionService.direction$.pipe(switchMap((dir: any) => {
      return of(this.getLabel(pageName, dir));
    }));
  }
}
